import clsx from "clsx";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { useMemo } from "react";

interface CheckOption {
	id: string | number;
	value: string;
	label?: string;
	icon?: React.ReactNode;
}

interface CheckGroupFieldProps {
	id: string;
	label: string;
	options: CheckOption[];
	// Controlled multi-select
	values?: string[];
	onChange?: (values: string[]) => void;
	required?: boolean;
	error?: string;
	disabled?: boolean;
	className?: string;
	gridCols?: number;
	showIcons?: boolean;
	disabledOptions?: string[];
	navRef?: boolean;
}

export function CheckGroupField({
	id,
	label,
	options,
	values,
	onChange,
	error,
	required = false,
	disabled = false,
	className = "",
	gridCols = 2,
	showIcons = false,
	disabledOptions = [],
	navRef = false,
}: CheckGroupFieldProps) {
	const checkAll = useMemo(() => new Set(["any", "everything"].map((v) => v.toLowerCase())), []);
	const selectedValues = values !== undefined && values.length > 0 ? values : [];
	const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

	const colClass =
		{
			1: "yF-grid-cols-1",
			2: "yF-grid-cols-2",
			3: "yF-grid-cols-3",
			4: "yF-grid-cols-4",
			5: "yF-grid-cols-5",
			6: "yF-grid-cols-6",
		}[gridCols] || "yF-grid-cols-2";

	const handleChange = (next: string[]) => {
		onChange?.(next);
	};

	const disabledSet = useMemo(() => new Set(disabledOptions), [disabledOptions]);

	// Map each option value to its original index for stable ordered selections
	const valueToIndex = useMemo(() => {
		const map = new Map<string, number>();
		options.forEach((opt, i) => map.set(opt.value, i));
		return map;
	}, [options]);

	return (
		<div className={className}>
			<div
				role="group"
				aria-label={label}
				aria-required={required}
				aria-describedby={error ? `${id}-error` : ""}
				aria-invalid={!!error}
				aria-disabled={disabled}
				id={id}
			>
				<div className="yF-label">{label}</div>
				<div
					className={clsx(
						navRef ? "yF-grid-cols-2" : colClass,
						`yF-grid yF-mt-1 yF-gap-x-2 yF-gap-y-2 md:yF-gap-x-2`,
					)}
				>
					{options.map((item, index) => {
						const isOptionDisabled = disabled || disabledSet.has(item.value);
						const selected = selectedSet.has(item.value);
						const toggle = () => {
							if (isOptionDisabled) return;

							// Toggle via Set for O(1) membership
							const nextSet = new Set(selectedValues);
							if (selected) {
								nextSet.delete(item.value);
							} else {
								nextSet.add(item.value);
							}

							let newValues = Array.from(nextSet);

							// Check if the option is a CheckAll option
							const hasCheckAll = newValues.some((value) =>
								checkAll.has(value.toLowerCase()),
							);
							if (hasCheckAll && checkAll.has(item.value.toLowerCase())) {
								newValues = [item.value];
							} else if (hasCheckAll) {
								newValues = newValues.filter((v) => !checkAll.has(v.toLowerCase()));
							}

							// Return values sorted according to its order.
							const sortedValues = [...newValues].sort((a, b) => {
								const ai = valueToIndex.get(a) ?? 0;
								const bi = valueToIndex.get(b) ?? 0;
								return ai - bi;
							});
							handleChange(sortedValues);
						};
						return (
							<div
								key={item.value}
								id={`${index}-option-${item.id}`}
								role="button"
								aria-pressed={selected}
								aria-disabled={isOptionDisabled}
								tabIndex={isOptionDisabled ? -1 : 0}
								onClick={toggle}
								className={clsx(
									isOptionDisabled
										? "yF-opacity-50 yF-cursor-not-allowed"
										: "yF-cursor-pointer",
									isOptionDisabled ? "yF-bg-gray-50" : "yF-bg-white",
									selected
										? "yF-border-[--yF-theme-color] yF-ring-1 yF-ring-[--yF-theme-color]"
										: "yF-border-gray-300",
									"yF-relative yF-flex yF-flex-1 yF-rounded-lg yF-border yF-p-2 yF-shadow-sm focus:yF-outline-none focus-visible:yF-outline-[--yF-theme-color] md:yF-p-3",
								)}
							>
								<span className="yF-flex yF-flex-1">
									<span
										className={clsx(
											navRef ? "" : "",
											"yF-flex yF-flex-col yF-items-start yF-justify-center",
										)}
									>
										{showIcons && item.icon && (
											<div
												className={clsx(
													!selected
														? " yF-text-gray-900"
														: "yF-text-[--yF-theme-color]",
												)}
											>
												{item.icon}
											</div>
										)}
										<span
											className={clsx(
												!selected
													? "yF-flex yF-font-medium yF-text-gray-900"
													: "",
												"yF-text-wrap yF-text-[--yF-theme-color] yF-flex yF-text-sm yF-font-medium yF-leading-4",
											)}
										>
											{item.label || item.value}
										</span>
									</span>
								</span>
								<CheckCircleIcon
									className={clsx(
										!selected ? "yF-invisible" : "",
										"yF-h-5 yF-w-5 yF-text-[--yF-theme-color]",
									)}
									aria-hidden="true"
									weight="fill"
								/>
								<span
									className={clsx(
										selected
											? "yF-border-[--yF-theme-color]"
											: "yF-border-transparent",
										"yF-pointer-events-none yF-absolute yF-rounded-lg",
									)}
									aria-hidden="true"
								/>
							</div>
						);
					})}
				</div>
			</div>
			{error && required && (
				<p
					id={`${id}-error`}
					className="yF-mt-1 yF-text-xs yF-text-red-600"
					role="alert"
					aria-live="polite"
				>
					{error}
				</p>
			)}
		</div>
	);
}
